import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, ElementRef, ViewChild, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BetterBuysConstants } from '@app/better-buys/constants/better-buys-constants.enum';
import { ClientListViewService } from '@app/lists/services/client-list-view.service';
import { MassDeleteDualPaneContainerComponent } from '@app/lists/shared/delete-list/mass-delete-dual-pane-container/mass-delete-dual-pane-container.component';
import { ListDownloadContainerComponent } from '@app/lists/shared/list-download-container/list-download-container.component';
import { ListPrintContainerComponent } from '@app/lists/shared/list-print-container/list-print-container.component';
import {
  IonInput,
  ViewDidEnter,
  ViewWillEnter,
  ViewWillLeave,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { FEATURES } from '@shared/constants/splitio-features';
import { BetterBuyDeclineEvent } from '@shared/models/better-buy-card.model';
import { DownloadListService } from '@shared/services/document/download-list/download-list.service';
import { ListKey } from '@usf/list-types';
import {
  DownloadListOptions,
  ListConstants,
  ListViewMode,
  ListViewService,
} from '@usf/ngrx-list';
import { LoadingState } from '@usf/ngrx-product';
import { combineLatest, Observable, Subscription, timer } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { Crumb } from 'src/app/shared/components/breadcrumbs/breadcrumbs.component';
import { BetterBuysService } from '../../../better-buys/services/better-buys.service';
import { ListTypes } from '../../../shared/constants/lists-constants';
import { PATHS } from '../../../shared/constants/paths';
import { UsfProductCardModeEnum } from '../../../shared/constants/usf-product-card-mode.enum';
import { Product } from '../../../shared/models/product.model';
import {
  EllipsisClickData,
  ProductClickData,
} from '../../../shared/models/usf-product-card.model';
import { ListHotkeyService } from '../../../shared/services/hotkey/list-hotkey.service';
import { ListProductCardService } from '../../../shared/services/product-card/list-product-card.service';
import { ListAnalyticsHelperService } from '../../services/list-analytics-helper.service';
import { ListsOverlayService } from '../../services/lists-overlay.service';
import {
  ItemTypes,
  ListDetailViewModel,
  ProductRow,
} from '../../shared/list-detail-management/model/list-detail-management-view.model';
import { createListKey } from '../../shared/list-detail-management/selectors/helpers/list-detail-management.selectors.helper';
import { ListDetailManagementService } from '../../shared/list-detail-management/services/list-detail-management.service';
import { UsfListDetailManagementHeights } from '../../../shared/constants/lists-constants';

@Component({
  selector: 'app-list-detail',
  templateUrl: './list-detail.page.html',
  styleUrls: ['../lists-pages-common-styles.scss', './list-detail.page.scss'],
})
export class ListDetailPage
  implements ViewWillEnter, ViewDidEnter, ViewWillLeave
{
  // Children components
  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
  @ViewChild('priceModeSidebar') sidebar: ElementRef;
  @ViewChild(MassDeleteDualPaneContainerComponent)
  massDeleteModal: MassDeleteDualPaneContainerComponent;
  @ViewChild(ListDownloadContainerComponent)
  listDownloadModal: ListDownloadContainerComponent;
  @ViewChild(ListPrintContainerComponent)
  listPrintModal: ListPrintContainerComponent;

  // Constants
  mode: UsfProductCardModeEnum = UsfProductCardModeEnum.listDetail;
  readonly ItemTypes = ItemTypes;
  readonly ListTypes = ListTypes;
  readonly ListViewMode = ListViewMode;

  // Component variables
  vm: ListDetailViewModel;
  listKey;
  sidebarTopPosition: number;
  searchKey = '';
  breadcrumbs: Crumb[];
  showTopButton = false;
  isSelectedListLoaded$: Observable<boolean>;
  previousSearchKey = '';
  copyListEnabled: boolean;

  // Next week date range for PME toggle
  sundayDate: string;
  saturdayDate: string;
  isFriday$: Observable<boolean>;

  // Subscriptions
  listDetailManagementSub$: Subscription = new Subscription();
  virtualScrollSub$: Subscription = new Subscription();
  tracePageLoadSub$: Subscription;
  currentQuantityInput: IonInput = undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    readonly panAppState: PanAppState,
    private hotKeyService: ListHotkeyService,
    readonly platformService: PlatformService,
    public translateService: TranslateService,
    private listAnalyticsHelperService: ListAnalyticsHelperService,
    public listDetailManagementService: ListDetailManagementService,
    private listProductCardService: ListProductCardService,
    public listsOverlayService: ListsOverlayService,
    private betterBuysService: BetterBuysService,
    private downloadListService: DownloadListService,
    private readonly clientListViewService: ClientListViewService,
    private readonly listViewService: ListViewService,
    private renderer: Renderer2,
  ) {}

  ionViewWillEnter() {
    this.currentQuantityInput = undefined;
    this.listKey = this.listDetailManagementService.getListIdFromRoute(
      this.route.snapshot,
    );
    this.updateListForUserPreferences();
    this.updateLastSelectedList();
    this.setPageTitle();
    this.setVirtualScrollSub();
    this.setBreadcrumbs();
    this.getViewModel();
    this.resetSearch();
    this.resetSortAndFilter();
    this.panAppState
      .featureEnabled(FEATURES.split_global_copy_list.name)
      .then(isEnabled => (this.copyListEnabled = isEnabled));
    this.isSelectedListLoaded$ =
      this.clientListViewService.isSelectedListLoaded$;

    this.sundayDate = this.listDetailManagementService.getNextSunday();
    this.saturdayDate = this.listDetailManagementService.getNextSaturday();
    this.isFriday$ = timer(0, 60000).pipe(map(() => new Date().getDay() === 5));
  }

  updateListForUserPreferences() {
    this.listDetailManagementService.updateRecentlyViewedUserPreferences(
      this.listKey?.listTypeId + '-' + this.listKey?.listId,
    );
  }

  updateLastSelectedList() {
    this.listDetailManagementService.updateLastSelectedList(
      this.listKey?.listTypeId,
      this.listKey?.listId,
    );
  }

  setPageTitle() {
    this.panAppState.setPageTitle(
      this.vm?.list?.listKey?.listTypeId === ListTypes.og
        ? this.translateService.instant('i18n.lists.orderGuide')
        : this.translateService.instant('i18n.lists.myLists'),
    );
  }

  setVirtualScrollSub() {
    this.sidebarTopPosition =
      this.platformService?.platformType ===
      this.platformService.platformEnum.desktop
        ? UsfListDetailManagementHeights.groupsSidebarBaseTopPositionDesktop
        : UsfListDetailManagementHeights.groupsSidebarBaseTopPositionTablet;

    this.virtualScrollSub$ = this.virtualScroll
      ?.elementScrolled()
      ?.subscribe(scrollEvent => {
        this.showTopButton =
          this.virtualScroll.measureScrollOffset('top') > 5 ? true : false;
        if (
          this.platformService.platformType !==
          this.platformService.platformEnum.mobile
        ) {
          const event = scrollEvent as any;
          this.adjustPriceModeSidebarTop(event);
        }
      });
  }

  adjustPriceModeSidebarTop(event: any) {
    let newTopPosition: number;

    if (
      this.platformService.platformType ===
      this.platformService.platformEnum.desktop
    ) {
      newTopPosition = 325 - event?.target?.scrollTop;
      if (newTopPosition < 20) {
        newTopPosition = 20;
      }
    } else if (
      this.platformService.platformType ===
      this.platformService.platformEnum.tablet
    ) {
      newTopPosition = 207 - event?.target?.scrollTop;
      if (newTopPosition < 15) {
        newTopPosition = 15;
      }
    }

    this.setPriceModeSidebarTopStyle(newTopPosition);
  }

  setPriceModeSidebarTopStyle(newTopPosition: number) {
    if (newTopPosition !== this.sidebarTopPosition) {
      this.sidebarTopPosition = newTopPosition;
      this.renderer?.setStyle(
        this.sidebar?.nativeElement,
        this.translateService.instant('i18n.common.measurements.top'),
        this.sidebarTopPosition.toString() +
          this.translateService.instant('i18n.common.measurements.px'),
      );
    }
  }

  setBreadcrumbs() {
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.myLists',
        url: '/desktop/lists',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.shoppingLists',
        url: '',
        active: true,
      },
    ];
  }

  getViewModel() {
    this.listDetailManagementSub$ = this.listDetailManagementService
      .selectListDetailVm$(this.listKey.listTypeId, this.listKey.listId)
      .subscribe(data => {
        this.setVmAndHotkeyData(data);
        this.dispatchEndSearchAction();
      });
  }

  setVmAndHotkeyData(viewModel: ListDetailViewModel) {
    this.vm = viewModel;
    this.hotKeyService.setItemHeights(viewModel?.itemHeights);
    this.hotKeyService.setProductCardIds(viewModel?.productCardIds);
    this.hotKeyService.setListId(viewModel?.list?.listKey?.listId?.toString());
  }

  dispatchEndSearchAction() {
    if (this.searchKey.length > 0) {
      this.listDetailManagementService.searchInListEnd(
        this.searchKey,
        this.listKey.listId,
        this.listKey.listTypeId,
      );
    }
  }

  search() {
    if (this.searchKey !== this.previousSearchKey) {
      this.listDetailManagementService.searchInList(
        this.searchKey,
        this.listKey.listId,
        this.listKey.listTypeId,
      );
      this.previousSearchKey = this.searchKey;
    }
  }

  async resetSearch() {
    this.clearFocus();
    setTimeout(this.searchTimeout, 50);
  }

  searchTimeout = () => {
    this.searchKey = '';
    this.search();
  };

  trackItem(index: any, item: any) {
    return item ? index : undefined;
  }

  ionViewDidEnter() {
    window.dispatchEvent(new Event('resize'));
    this.tracePageLoad();
    this.trackPageLoad();
  }

  trackPageLoad() {
    combineLatest([
      this.listDetailManagementService.isListAndProductAndPricingAndAlternativePricingDataLoaded$(
        this.listKey.listTypeId,
        this.listKey.listId,
      ),
      this.listDetailManagementService.selectListDetailVm$(
        this.listKey.listTypeId,
        this.listKey.listId,
      ),
    ])
      .pipe(
        filter(([loadingState, listDetailViewModel]) => loadingState === true),
        take(1),
      )
      .subscribe(([loadingState, listDetailViewModel]) => {
        if (loadingState === true) {
          this.listAnalyticsHelperService.trackListDetailPageLoad(
            createListKey(listDetailViewModel.list.listKey),
            listDetailViewModel.items,
          );
        }
      });
  }

  tracePageLoad() {
    this.tracePageLoadSub$ = this.listDetailManagementService
      .selectListDetailVm$(this.listKey.listTypeId, this.listKey.listId)
      .pipe(
        filter(
          (listDetailViewModel: ListDetailViewModel) =>
            listDetailViewModel.productLoadingState === LoadingState.loaded ||
            listDetailViewModel.productLoadingState === LoadingState.error,
        ),
        take(1),
      )
      .subscribe((listDetailViewModel: ListDetailViewModel) => {
        if (listDetailViewModel.productLoadingState === LoadingState.loaded) {
          this.handleSuccessfulPageLoad(listDetailViewModel);
        }
        if (listDetailViewModel.productLoadingState === LoadingState.error) {
          this.handleErrorForPageLoad(listDetailViewModel);
        }
      });
  }

  handleSuccessfulPageLoad(listDetailViewModel: ListDetailViewModel) {
    this.listDetailManagementService.displayListDetailsSuccessTrace(
      listDetailViewModel.list.listKey.listTypeId,
      String(listDetailViewModel.list.listKey.listId),
      listDetailViewModel.list.listItemCount,
    );
  }

  handleErrorForPageLoad(listDetailViewModel: ListDetailViewModel) {
    this.listDetailManagementService.displayListDetailsFailureTrace(
      listDetailViewModel.list.listKey.listTypeId,
      String(listDetailViewModel.list.listKey.listId),
      listDetailViewModel.list.listItemCount,
    );
    this.listsOverlayService.showListLoadingErrorToast();
    this.routeTo('/lists');
  }

  ionViewWillLeave() {
    this.handleSubscriptions();
    this.resetSearch();
  }

  handleSubscriptions() {
    this.currentQuantityInput = undefined;
    this.listDetailManagementSub$.unsubscribe();
    this.virtualScrollSub$.unsubscribe();
    this.tracePageLoadSub$?.unsubscribe();
  }

  onAddProductAction() {
    this.listDetailManagementService.goToSearchPage();
  }

  goToTop() {
    this.platformService?.platformType !==
    this.platformService.platformEnum.mobile
      ? this.virtualScroll.scrollToIndex(0)
      : this.virtualScroll.scrollToIndex(0, 'smooth');
  }

  quantityFocusHandler(input: IonInput) {
    this.currentQuantityInput = input;
  }

  clearFocus() {
    if (this.currentQuantityInput) {
      this.currentQuantityInput.getInputElement().then(element => {
        element.blur();
      });
      this.currentQuantityInput = undefined;
    }
  }

  async changeQuantityHandler(event: any) {
    this.listAnalyticsHelperService.listDetailAddToOrder(
      event.quantityUpdate,
      this.vm.list,
      event.product,
      event.alternative,
    );
  }

  async openSortAndFilterListModal() {
    const ret = this.listsOverlayService.openSortAndFilterListModal(
      this.vm?.groupNames,
      this.vm?.productClasses,
      this.vm?.listSearchSortFilterOptions?.sortType,
      this.vm?.listSearchSortFilterOptions?.filters,
      this.vm?.listSearchSortFilterOptions?.groupToSort,
      false,
      false,
    );
    (await ret).onDidDismiss().then(res => {
      if (res.data) {
        const sortData = res as any;
        this.listDetailManagementService.sortAndFilterInList(
          sortData.data.sortMethod,
          sortData.data.groupToSort,
          sortData.data.filters,
        );
        this.listDetailManagementService.updateSortAndFilterUserPreferences(
          this.listKey.listTypeId + '-' + this.listKey.listId,
          sortData.data.sortMethod,
          sortData.data.groupToSort,
          sortData.data.filters,
        );
      }
    });
  }

  resetSortAndFilter() {
    this.listDetailManagementService.sortAndFilterInList(
      ListConstants.groupLine,
      ListConstants.allGroups,
      [],
    );
  }

  routeTo(path: string, queryParams = {}) {
    this.router.navigate([path], { queryParams, relativeTo: this.route });
  }

  async routeToPDP(productClickData: ProductClickData) {
    if (productClickData.betterBuyCard) {
      this.listProductCardService.routeToPDPBetterBuyCard(
        productClickData.productNumber,
        productClickData.currentProduct,
      );
    } else {
      this.listProductCardService.routeToPDP(
        productClickData.productNumber,
        productClickData.isQuickView,
      );
    }
  }

  async openListOptions(event: Event) {
    await this.listsOverlayService.openListOptions(
      event,
      [
        PATHS.LISTS,
        'management',
        this.vm.list.listKey.listTypeId + '-' + this.vm.list.listKey.listId,
      ],
      this.vm.list,
      this.vm.list.listName,
      this.vm.list.listKey.listTypeId,
      true,
      this.vm?.downloadPrintFlag,
      true,
      this.massDeleteModal,
      this.listDownloadModal,
    );
  }

  openDownloadPrintModal(download: boolean) {
    const listName =
      this.vm.list?.listKey?.listTypeId === 'OG'
        ? `Order Guide #${this.vm.list?.listKey?.listId}`
        : this.vm.list?.listName;
    if (download) {
      this.listsOverlayService.openDownloadListModal(
        this.vm.list.listKey.listId,
        this.vm.list.listKey.listTypeId,
        listName,
        this.listDownloadModal,
      );
    } else {
      this.listsOverlayService.openPrintListModal(
        this.vm.list.listKey.listId,
        this.vm.list.listKey.listTypeId,
        listName,
        this.vm.list.customerNumber,
        this.vm.list?.listState,
        this.listPrintModal,
      );
    }
  }

  async openProductCardEllipsis(ellipsisClickData: EllipsisClickData) {
    const buttons = await this.listProductCardService.constructEllipsisButtons(
      ellipsisClickData,
      this.vm,
    );

    this.listProductCardService.openProductCardEllipsis(
      buttons,
      ellipsisClickData.isTouch,
      ellipsisClickData.event,
    );
  }

  async openQuickView(ellipsisClickData: EllipsisClickData) {
    this.listProductCardService.openQuickViewModal(
      ellipsisClickData,
      'quickView',
    );
  }

  async openItemNotes(ellipsisClickData: EllipsisClickData) {
    const noteExists =
      ellipsisClickData.product.item?.note !== undefined &&
      ellipsisClickData.product.item?.note !== '' &&
      ellipsisClickData.product.item?.note !== null;
    if (this.vm?.listItemNoteFeatureFlag) {
      this.listProductCardService.directlyOpenCreateEditNote(
        noteExists,
        ellipsisClickData,
      );
    } else {
      this.listProductCardService.openQuickViewModal(
        ellipsisClickData,
        'quickView',
      );
    }
  }

  async clickPromotions(event: EllipsisClickData) {
    this.listProductCardService.openQuickViewModal(event, 'productStatus');
  }

  async openProductStatus(ellipsisClickData: EllipsisClickData) {
    this.listProductCardService.showProductStatuses(ellipsisClickData);
  }

  handleBetterBuyDeclineClick(betterBuyDeclineEvent: BetterBuyDeclineEvent) {
    this.betterBuysService.openDeclineModal(betterBuyDeclineEvent);
  }
  acceptSwap(product: Product) {
    this.betterBuysService.openAcceptSwapModal(
      product,
      false,
      BetterBuysConstants.listsCapability,
    );
  }

  closeDownloadListModal() {
    this.listsOverlayService.closeDownloadListModal();
  }

  downloadList(options: DownloadListOptions) {
    this.listsOverlayService.closeDownloadListModal();
    this.listDetailManagementService.downloadList(options);
  }

  closeImportListErrorModal() {
    this.listsOverlayService.listImportErrorsModal$.next(false);
  }

  downloadImportErrors() {
    this.listsOverlayService.listImportErrorsModal$.next(false);
    this.downloadListService.downloadImportErrors(
      this.vm?.importErrors?.errors ?? [],
      this.vm?.list?.listName ?? '',
    );
  }

  openImportListErrorModal() {
    this.listsOverlayService.listImportErrorsModal$.next(true);
  }

  removeImportErrors(listKey: ListKey) {
    this.listDetailManagementService.removeImportError(listKey);
  }

  handleOpenSortAndFilterListModal(event: any) {
    this.openSortAndFilterListModal();
  }

  handleClearSortAndFilterListModalEmitter(event: any) {
    this.resetSortAndFilter();

    this.listDetailManagementService.updateSortAndFilterUserPreferences(
      this.listKey.listTypeId + '-' + this.listKey.listId,
      ListConstants.groupLine,
      ListConstants.allGroups,
      [],
    );
  }
  handleProductConversionClick(product: ProductRow) {
    this.listProductCardService.handleProductConversionClick(product, false);
  }

  updateListViewMode(event: any) {
    const listViewMode = event.detail.value as ListViewMode;
    this.listViewService.changeViewMode(listViewMode);
  }

  handlePmeToggleChange(fridayPricingToggleIsOn: boolean) {
    // Update toggle state
    this.listDetailManagementService.updateToggleState();
    // Pass to service
    setTimeout(() => {
      this.listDetailManagementService.updatePricingWithFridayOption(
        this.vm?.selectedListViewModel?.productNumbers,
        fridayPricingToggleIsOn,
        this.vm?.selectedListViewModel?.selectedListType,
        this.vm?.selectedListViewModel?.selectedListId,
      );
    }, 100);
  }
}
